// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useState, useContext, SyntheticEvent } from 'react';
import copy from 'copy-to-clipboard';
import { useRouter } from 'next/router';

import { apiPath } from 'presentations/routes/path';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import { post, patch } from 'infras/rest_client';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  ApiKeyCreateBody,
  ApiKeyItem,
  ApiKeyUpdateBody,
} from 'presentations/api/schema/api_key';
import { ErrorList } from 'domains/models';
import { getDateTime } from 'infras/datetime';

interface Props {
  viewer: ViewerItem;
  apiKey: ApiKeyItem | null;
  onClose?: any;
  onUpdate?: any;
  onCreate?: any;
}

export const unlimitedApiKeyDuration = 999999;

export function useApiKeyDialog(props: Props) {
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [name, setName] = useState<string | null>(props.apiKey?.name || null);
  const [expiresDate, setExpiresDate] = useState<number>(30);
  const [errors, setErrors] = useState<ErrorList>({});
  const [rawApiKey, setRawApiKey] = useState<string | null>(null);

  const onChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const prop = String(e.currentTarget.name);
    const value = e.currentTarget.value;
    switch (prop) {
      case 'name':
        setName(value);
        // setIsDisabled(value ? false : true);
        break;
      case 'expiresDate':
        setExpiresDate(Number(value));
        break;
      default:
        return;
    }
  };

  const onCopy = async () => {
    if (rawApiKey) {
      copy(rawApiKey, undefined);
      setIsCopied(true);
    }
  };

  const onSubmit = async () => {
    if (isLoading) return;
    if (!name || !expiresDate) return;
    setIsLoading(true);
    if (props.apiKey) {
      const resource = {
        ...props.apiKey,
        name,
      };
      const data: ApiKeyUpdateBody = {
        resource,
      };
      const result = await patch(
        apiPath(
          `/v1/teams/${props.viewer.team!.uid}/api_keys/${props.apiKey.uid}`,
        ),
        { data },
      );
      if (!result.isSuccess) {
        setErrors(result.errors);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      if (props.onUpdate) props.onUpdate();
      props.onClose();
    } else {
      const data: ApiKeyCreateBody = {
        name,
        isNeverExpires: true,
        expiresAt:
          expiresDate === unlimitedApiKeyDuration
            ? null
            : getDateTime().plus({ days: expiresDate }).toISO(),
      };

      const result = await post(
        apiPath(`/v1/teams/${props.viewer.team!.uid}/api_keys`),
        {
          data,
        },
      );
      if (!result.isSuccess) {
        if (result.status === 400) {
          setErrors(result.errors);
        }
        setIsLoading(false);
        return;
      }
      if (props.onCreate) props.onCreate();
      setRawApiKey(result.data.rawApiKey);
    }

    setIsLoading(false);
  };

  return {
    state: {
      isLoading,
      isCopied,
      name,
      expiresDate,
      rawApiKey,
      errors,
    },
    actions: {
      onChange,
      onSubmit,
      onCopy,
    },
  };
}
